"use client";

import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  v1 as uuidv1,
  v3 as uuidv3,
  v4 as uuidv4,
  v5 as uuidv5,
  v6 as uuidv6,
  v7 as uuidv7,
} from "uuid";
import { UUIDGeneratorState, INITIAL_STATE, UUIDVersion } from "./components/types";
import UUIDGeneratorForm from "./components/UUIDGeneratorForm";
import UUIDResults from "./components/UUIDResults";
import UUIDInformation from "./components/UUIDInformation";

export default function UUIDGeneratorPage() {
  const { user } = useAuth();
  const [state, setState] = useState<UUIDGeneratorState>(INITIAL_STATE);

  // Update state helper
  const updateState = useCallback((update: Partial<UUIDGeneratorState>) => {
    setState(prev => ({ ...prev, ...update }));
  }, []);

  const generateUUID = (version: UUIDVersion): string => {
    switch (version) {
      case "v1":
        return uuidv1();
      case "v3":
        return uuidv3(state.name || "default", "6ba7b810-9dad-11d1-80b4-00c04fd430c8");
      case "v4":
        return uuidv4();
      case "v5":
        return uuidv5(state.name || "default", state.namespace);
      case "v6":
        return uuidv6();
      case "v7":
        return uuidv7();
      default:
        return uuidv4();
    }
  };

  const generateUUIDs = useCallback(() => {
    const uuids: string[] = [];
    for (let i = 0; i < state.quantity; i++) {
      uuids.push(generateUUID(state.uuidVersion));
    }
    updateState({ generatedUUIDs: uuids });

    // Best-effort server-side log for history
    if (user) {
      fetch('/api/activity/log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activityType: 'uuid_generated',
          toolName: 'uuid-generator',
          // Limit uuids stored to avoid very large payloads
          metadata: { version: state.uuidVersion, quantity: state.quantity, uuids: uuids.slice(0, 100) },
        }),
      }).catch(() => {});
    }
  }, [state.quantity, state.uuidVersion, state.name, state.namespace, user, updateState]);

  const downloadToFile = useCallback(() => {
    if (state.generatedUUIDs.length === 0) return;
    
    const content = state.generatedUUIDs.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uuids-${state.uuidVersion}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [state.generatedUUIDs, state.uuidVersion]);

  const clearAll = useCallback(() => {
    updateState({ generatedUUIDs: [], copiedIndex: null });
  }, [updateState]);

  const loadSampleNamespace = useCallback(() => {
    updateState({ 
      namespace: "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
      name: "example.com"
    });
  }, [updateState]);

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">UUID Generator</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Generate UUIDs (Universally Unique Identifiers) in different versions for your projects
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <UUIDGeneratorForm
            state={state}
            updateState={updateState}
            onGenerate={generateUUIDs}
            onClear={clearAll}
            onLoadSampleNamespace={loadSampleNamespace}
          />
          
          <UUIDResults
            state={state}
            onDownload={downloadToFile}
          />
        </div>

        <UUIDInformation />
      </div>
    </div>
  );
}